/**
 * HeaderController
 * front-page 以外は #header を常に .show 状態で固定。
 * front-page でのみスクロール位置で表示/非表示をトグルする。
 *
 * front-page 時のしきい値 (ヒステリシス付き):
 *   .hide → .show : scrollY >= #top.offsetHeight
 *   .show → .hide : scrollY <  #top.offsetHeight - DEADBAND
 *   境界付近で Lenis のサブピクセル揺れや iOS Safari のアドレスバー伸縮による
 *   scrollY の微振動があってもトグルしないよう、上下しきい値の間にデッドバンドを設ける。
 *
 * iOS Safari のアドレスバー伸縮中は window.innerHeight や lvh 依存の
 * 要素高さがブレるので、しきい値は init / orientationchange 時のみ
 * 再計算してキャッシュを使う (resize は iOS のバー伸縮でも発火するため聞かない)。
 *
 * Barba SPA 遷移で init が繰り返し呼ばれるため、
 * 前回登録した listener を毎回破棄してから貼り直す。
 */
import { LenisController } from './lenis-controller';

export const HeaderController = {
  // 前回 init 時に登録した cleanup。SPA 遷移のたびに実行してからやり直す。
  _cleanup: null,
  // 現在の scrollY と threshold を再計測して .show / .hide を確定させる。
  // barba 遷移後、Lenis.scrollTo(immediate:true) の scroll event が
  // 発火せず HeaderController の内部状態が実表示と乖離するケースがあるため
  // 外部から強制的に再評価するための口を用意しておく。
  _refresh: null,

  refresh() {
    this._refresh?.();
  },

  init( container ) {
    // 前回分の listener / probe を必ず解除
    this._cleanup?.();
    this._cleanup = null;
    this._refresh = null;

    const header = document.querySelector( '#header' );
    if ( !header ) return;
    // #site-branding は #header の外に出しているが、show/hide は同期させる
    const branding = document.querySelector( '#site-branding' );

    const setShow = ( show ) => {
      header.classList.toggle( 'show', show );
      header.classList.toggle( 'hide', !show );
      branding?.classList.toggle( 'show', show );
      branding?.classList.toggle( 'hide', !show );
    };

    // barba の beforeEnter から渡される next.container を優先。
    //   遷移中に旧/新 container が一時的に DOM 上で共存すると
    //   document.querySelector('main#front-page') が旧コンテナに hit してしまい、
    //   新ページ (news 等) に居るのに front-page 扱いになる問題が起きる。
    //   → 明示的に渡された container の id で判定する。
    const current = container || document.querySelector( 'main[data-barba="container"]' );
    const isFrontPage = current?.id === 'front-page';

    if ( !isFrontPage ) {
      // front-page 以外は常時表示
      setShow( true );
      return;
    }

    // ---- front-page: スクロール位置でトグル (ヒステリシス) ----

    // FV (#top) を通過してから .show を付ける。
    //   iOS Safari のアドレスバー伸縮で window.innerHeight や lvh 依存の
    //   要素高さがブレるため、都度計測すると閾値もブレて FV 内でチラつく。
    //   → init / orientationchange のときにだけ再計算してキャッシュする。
    //   (resize は iOS のバー伸縮でも発火するので購読しない)
    // さらに Lenis のサブピクセルスクロールで scrollY が境界付近を
    // 微小に往復するとトグルが連発するので、上下しきい値の間に
    // デッドバンド (DEADBAND px) を挟んで hysteresis させる。
    const topSection = current.querySelector( 'section#top' );
    const DEADBAND = 120;

    let threshold = 0;
    let shown = false;
    // barba 遷移後も Lenis.off が効かず listener が生き残るケースへの保険。
    // cleanup 時に disposed=true にして、以後 listener が発火しても no-op にする。
    let disposed = false;
    const measureThreshold = () => {
      threshold = topSection ? topSection.offsetHeight : window.innerHeight;
    };
    measureThreshold();

    // Barba 遷移で戻ってきたときは DOM 側に前ページの .show / .hide が
    // 残っていることがある。初期状態は現在の scrollY を根拠に確定させて、
    // 内部 shown フラグと DOM 表示を一致させる。
    shown = window.scrollY >= threshold;
    setShow( shown );

    // 外部から現在位置を根拠に .show / .hide を強制同期させる口
    //   barba の after フックから呼び、scrollTo 完了後の scrollY に
    //   基づき hysteresis を無視して確定させる (ヒステリシス無視: DEADBAND 中でも判定)
    this._refresh = () => {
      if ( disposed ) return;
      measureThreshold();
      const y = window.scrollY;
      const nextShown = y >= threshold;
      if ( nextShown !== shown ) {
        shown = nextShown;
        setShow( shown );
      }
    };

    const apply = ( y ) => {
      if ( disposed ) return;
      // 二重防御: 呼ばれた時点で container が front-page でなければ何もしない。
      //   disposed / Lenis.off が効かず古い listener が生き残ってしまった場合でも、
      //   非 front-page で誤って setShow(false) が走らないようにするための保険。
      const cur = document.querySelector( 'main[data-barba="container"]' );
      if ( cur?.id !== 'front-page' ) return;
      // 現状態に応じて片側だけ判定 → デッドバンドの中はトグルしない
      if ( shown ) {
        if ( y < threshold - DEADBAND ) {
          shown = false;
          setShow( false );
        }
      } else {
        if ( y >= threshold ) {
          shown = true;
          setShow( true );
        }
      }
    };

    const onScroll = ( arg ) => {
      if ( disposed ) return;
      // Lenis の scroll イベントは { scroll } を渡す。window 側は引数なし。
      const y = ( arg && typeof arg.scroll === 'number' ) ? arg.scroll : window.scrollY;
      apply( y );
    };
    const onOrientation = () => {
      if ( disposed ) return;
      measureThreshold();
      apply( window.scrollY );
    };

    const lenis = LenisController.lenis;
    lenis?.on( 'scroll', onScroll );
    // Lenis がスクロールを乗っ取っても window 側のフォールバックとして両方バインド
    window.addEventListener( 'scroll', onScroll, { passive: true } );
    window.addEventListener( 'orientationchange', onOrientation );

    // SPA 遷移時にこの init を再呼び出しされた際の後始末
    this._cleanup = () => {
      disposed = true;
      this._refresh = null;
      lenis?.off?.( 'scroll', onScroll );
      window.removeEventListener( 'scroll', onScroll );
      window.removeEventListener( 'orientationchange', onOrientation );
    };
  },
};
